import type { AdminUserContext } from "@/types/auth";
import { emptyAdminUserContext } from "@/types/auth";
import { create } from "zustand";

/**
 * Sync mirror of the admin user-context for permission checks that need to
 * happen outside React render (route guards, imperative code). The
 * `useAdminUserContext` hook updates this store from the TanStack Query
 * cache so both sources stay aligned.
 *
 * Use the helper functions (`hasPermission`, `hasAnyPermission`) for
 * permission checks rather than reading the raw store state — they handle
 * the "context not loaded yet" case correctly (return false).
 */

interface AdminUserContextState {
	context: AdminUserContext;
	isLoaded: boolean;
	setContext: (context: AdminUserContext) => void;
	clear: () => void;
}

export const useAdminUserContextStore = create<AdminUserContextState>((set) => ({
	context: emptyAdminUserContext,
	isLoaded: false,
	setContext: (context) => set({ context, isLoaded: true }),
	clear: () => set({ context: emptyAdminUserContext, isLoaded: false }),
}));

/* -------------------------------------------------------------------------- */
/* Sync helpers — use these from anywhere (React or not)                      */
/* -------------------------------------------------------------------------- */

export const hasPermission = (permission: string): boolean => {
	const { context, isLoaded } = useAdminUserContextStore.getState();
	if (!isLoaded) return false;
	return context.permissions.includes(permission);
};

export const hasAnyPermission = (permissions: readonly string[]): boolean => {
	const { context, isLoaded } = useAdminUserContextStore.getState();
	if (!isLoaded) return false;
	return permissions.some((p) => context.permissions.includes(p));
};

export const hasAllPermissions = (permissions: readonly string[]): boolean => {
	const { context, isLoaded } = useAdminUserContextStore.getState();
	if (!isLoaded) return false;
	return permissions.every((p) => context.permissions.includes(p));
};
